import type { CollectionBindingOverride } from '@vibesites/runtime';

/**
 * Gallery helpers: photo src, marketing-graphic filters, and tab grouping.
 * Binding / editor order is preserved — no programmatic tour resequencing.
 * Downstream sites with a saved gallery reorder keep that order unchanged.
 */

export interface CuratableGalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  alt_text?: string | null;
  title?: string | null;
  description?: string | null;
}

export type GalleryTab = 'all' | 'community' | 'apartment';

export interface CuratedGallery<T> {
  all: T[];
  community: T[];
  apartment: T[];
}

type PhotoTab = 'community' | 'apartment' | null;

const DRONE = /\b(drones?|aerials?|bird'?s?\s*-?\s*eye|from\s+above)\b/;
const MONUMENT =
  /\b(monuments?|property signs?|entry signs?|community signs?|signage|monument signs?)\b/;
const EXTERIOR =
  /\b(exteriors?|façades?|facades?|elevations?|streetscapes?|arrivals?|buildings?|courtyards?|porte[\s-]?coch[eè]re|driveways?|architecture|landscap(?:e|ing)|twilight|dusk|sunset|golden\s+hour|community\s+entrances?|leasing\s+entrances?|(?:building|property|community)\s+views?)\b|\bbuilding\s+exterior\b|\bproperty\s+exterior\b/;
const STAGED =
  /virtually\s+staged|virtual(?:ly)?\s+stag|virtually\s+furnish|\bstaged\b|\bfurnished\b|\bmodel\s+(?:apartments?|units?|homes?|kitchens?|livings?|bedrooms?|baths?)/;
const VACANT = /\bvacants?\b|\bunfurnished\b|\bempty\s+(?:apartments?|units?|kitchens?|rooms?|livings?)/;
/** Logos / brand marks that should never appear as page heroes. */
const LOGO = /\blogos?\b|\bwordmarks?\b|\blogotypes?\b|\bbrand\s+marks?\b/;
/**
 * Unit floor-plan drawings / line art (not community site maps). These belong
 * on floorplan listings, not marketing heroes or lifestyle pickers.
 */
const FLOOR_PLAN_LAYOUT =
  /\bfloor\s*-?\s*plans?\b|\bfloorplans?\b|\bunit\s*-?\s*plans?\b|\bblueprints?\b|\bschemat(?:ic|ics)\b|\bline\s*arts?\b|\blayout\s+drawings?\b/;
const CLUBHOUSE = /\b(clubhouses?|club\s*rooms?|resident lounges?|resident clubs?|leasing offices?)\b|\blobb(?:y|ies)\b/;
const LOUNGE = /\blounges?\b/;
const COWORK = /\b(cowork(?:ing)?|co-working|business centers?|conference rooms?)\b/;
const FITNESS = /\b(fitness|gyms?|workouts?|exercise rooms?|yoga)\b/;
const POOL = /\b(pools?|swimming|hot tubs?|cabanas?|sundecks?|sun decks?)\b|\bspas?\b/;
const GRILL = /\b(grills?|bbqs?|barbecues?|barbeques?|outdoor kitchens?|fire\s*pits?|picnics?)\b/;
const OUTDOOR_SEATING =
  /\b(patios?|umbrellas?|outdoor\s+(?:seat(?:ing|s)?|dining|lounges?)|picnic\s+(?:tables?|areas?)|cafe\s+tables?|bistro|terrace\s+seat|fireside\s+patios?|seating\s+areas?)/;
const PET = /\b(dog parks?|pet parks?|pet spas?|pet wash(?:es)?|bark parks?|dog wash(?:es)?)\b/;
const RECREATION =
  /\b(playgrounds?|basketball|tennis|volleyball|sports? courts?|game rooms?|billiards?|recreation|putting greens?|bocce)\b/;
const PACKAGE = /\b(packages?|parcels?|mail\s*rooms?)\b|\b(?:package|parcel)\s+lockers?\b/;
const APARTMENT_ROOM =
  /\b(kitchens?|bedrooms?|bathrooms?|baths?|living\s+(?:rooms?|spaces?|areas?)|dining\s+rooms?|islands?|peninsulas?|appliances?)\b/;

export function galleryPhotoSrc(photo: CuratableGalleryPhoto): string {
  return photo.image || photo.original_url || '';
}

/**
 * True when a collection-binding override saved a custom item order.
 * Downstream property themes that reordered the gallery in the editor must
 * keep that sequence — never re-apply tour curation on top of it.
 */
export function hasCustomGalleryOrder(
  override: CollectionBindingOverride | null | undefined,
): boolean {
  if (!override) return false;
  return override.orderedIds != null || (override.ordering?.length ?? 0) > 0;
}

function normalizePhotoText(parts: Array<string | null | undefined>): string {
  return parts
    .map((value) => (value || '').toLowerCase())
    .join(' ')
    .replace(/[/_+.-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Caption plus URL filename tokens. CDN paths often include subject cues
 * (patio, exterior, drone) when CMS captions are thin.
 */
function photoText(photo: CuratableGalleryPhoto): string {
  const urlName = [photo.image, photo.original_url]
    .map((value) => {
      if (!value) return '';
      try {
        const path = value.split('?')[0] ?? value;
        const leaf = path.split('/').pop() ?? path;
        return leaf;
      } catch {
        return value;
      }
    })
    .join(' ');
  return normalizePhotoText([photo.title, photo.description, photo.alt_text, urlName]);
}

/** True when caption/title text looks like a logo or brand mark. */
export function isLogoPhoto(photo: CuratableGalleryPhoto): boolean {
  return LOGO.test(photoText(photo));
}

/** True when caption/title text looks like a unit floor-plan drawing / line art. */
export function isFloorPlanLayoutPhoto(photo: CuratableGalleryPhoto): boolean {
  return FLOOR_PLAN_LAYOUT.test(photoText(photo));
}

/**
 * Logos and unit floor-plan layouts are not marketing photography — exclude
 * them from heroes and lifestyle pickers.
 */
export function isNonMarketingGraphic(photo: CuratableGalleryPhoto): boolean {
  return isLogoPhoto(photo) || isFloorPlanLayoutPhoto(photo);
}

/** Virtually staged / furnished / model-unit photography. */
export function isStagedPhoto(photo: CuratableGalleryPhoto): boolean {
  return STAGED.test(photoText(photo));
}

function looksLikeAmenity(text: string): boolean {
  return (
    PET.test(text) ||
    PACKAGE.test(text) ||
    POOL.test(text) ||
    GRILL.test(text) ||
    OUTDOOR_SEATING.test(text) ||
    CLUBHOUSE.test(text) ||
    (LOUNGE.test(text) && !POOL.test(text)) ||
    COWORK.test(text) ||
    FITNESS.test(text) ||
    RECREATION.test(text)
  );
}

function looksLikeCommunity(text: string): boolean {
  return (
    DRONE.test(text) ||
    MONUMENT.test(text) ||
    EXTERIOR.test(text) ||
    looksLikeAmenity(text) ||
    /\blifestyle\b/.test(text)
  );
}

function looksLikeApartment(text: string): boolean {
  return APARTMENT_ROOM.test(text) || STAGED.test(text) || VACANT.test(text);
}

function classifyTab(photo: CuratableGalleryPhoto): PhotoTab {
  const text = photoText(photo);
  const title = (photo.title || '').toLowerCase();
  const apartmentTitle = title.includes('apartment');
  const communityTitle = title.includes('community') || title.includes('lifestyle');
  const apartmentish = apartmentTitle || looksLikeApartment(text);
  const communityish = communityTitle || looksLikeCommunity(text);

  if (apartmentTitle || (apartmentish && !communityish)) return 'apartment';
  if (communityish) return 'community';
  return null;
}

function usablePhotos<T extends CuratableGalleryPhoto>(photos: T[]): T[] {
  return photos.filter((photo) => galleryPhotoSrc(photo) && !isNonMarketingGraphic(photo));
}

/**
 * Split a binding-/editor-ordered gallery into tabs without resequencing.
 * Same path used for saved custom orders and for the default (no tour sort).
 */
export function groupGalleryByTab<T extends CuratableGalleryPhoto>(photos: T[]): CuratedGallery<T> {
  const usable = usablePhotos(photos);
  const community: T[] = [];
  const apartment: T[] = [];
  usable.forEach((photo) => {
    const tab = classifyTab(photo);
    if (tab === 'community') community.push(photo);
    else if (tab === 'apartment') apartment.push(photo);
  });
  return { all: usable, community, apartment };
}
